import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GovernancePage() {
  const [leadership, setLeadership] = useState([]);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const [leadershipRes, boardRes] = await Promise.all([
          axios.get(`${API}/api/leadership`),
          axios.get(`${API}/api/board`)
        ]);
        setLeadership(leadershipRes.data);
        setBoard(boardRes.data);
      } catch (error) {
        console.error('Failed to fetch governance data:', error);
      }
    }
    fetchData();
  }, []);

  return (
    <div className="bg-white pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Governance</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6">
            Leadership & Governance
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Strong corporate governance is fundamental to our approach. We maintain high standards 
            of transparency, accountability, and ethical conduct across all our activities.
          </p>
        </motion.div>
      </div>

      {/* Executive Team */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">Executive Team</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {leadership.map((member, index) => (
            <motion.div
              key={member.member_id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              data-testid={`leadership-${member.member_id}`}
            >
              <div className="aspect-[3/4] overflow-hidden mb-4 bg-slate-100">
                <img
                  src={member.image_url}
                  alt={member.name}
                  className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <h3 className="font-medium text-[#0A192F] mb-1">{member.name}</h3>
              <p className="text-sm text-slate-500 mb-3">{member.role}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{member.bio}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Board of Directors */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">Board of Directors</h2>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 pr-8 text-xs uppercase tracking-wider text-slate-500 font-medium">Name</th>
                  <th className="text-left py-4 pr-8 text-xs uppercase tracking-wider text-slate-500 font-medium">Position</th>
                  <th className="text-left py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Type</th>
                </tr>
              </thead>
              <tbody>
                {board.map((member, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="border-b border-slate-100"
                  >
                    <td className="py-4 pr-8 font-medium text-[#0A192F]">{member.name}</td>
                    <td className="py-4 pr-8 text-slate-600">{member.role}</td>
                    <td className="py-4">
                      <span className={`px-3 py-1 text-xs ${
                        member.type === 'Independent' ? 'bg-blue-100 text-blue-700' :
                        member.type === 'Executive' ? 'bg-slate-200 text-slate-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {member.type}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Governance Framework */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">Governance Framework</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Board Committees',
                items: ['Audit Committee', 'Remuneration Committee', 'Nomination Committee', 'Investment Committee']
              },
              {
                title: 'Compliance',
                items: ['Code of Conduct', 'Anti-Corruption Policy', 'Whistleblower Policy', 'Data Protection']
              },
              {
                title: 'Risk Management',
                items: ['Enterprise Risk Framework', 'Internal Controls', 'Regular Audits', 'Reporting Standards']
              }
            ].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 border border-slate-200"
              >
                <h3 className="font-medium text-[#0A192F] mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 bg-[#0A192F]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
